import {
  Badge,
  BlockStack,
  Box,
  Button,
  Icon,
  InlineStack,
  Popover,
  Tooltip,
  Text,
  Form,
  TextField,
  Link,
  Card,
  ChoiceList,
  Divider,
  DescriptionList,
  Tag,
  Select,
  Tabs,
  DataTable,
  SkeletonBodyText,
  SkeletonDisplayText,
  Grid,
} from '@shopify/polaris';
import QuickSearchSeating from 'components/quickSearchSeating';
import QuickSearchVehicle from 'components/quickSearchVehicle';
import SeatIndicator from 'components/SeatIndicator';
import { useDeleteConfirm } from 'context/DeleteConfirmContext';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { TypedSeating } from 'queries/seating.query';

import {
  ENUM_TRIP_MANIFEST_STATUS,
  tripStatusHelptext,
  tripStatusLabels,
  TypedTripManifest,
  useGetTrip_manifests,
  useMutateAssign_driver_to_trip,
  useMutateCreateTrip_manifest,
  useMutateDeleteTrip_manifest,
  useMutateRemove_driver_from_trip,
  useMutateUpdateTrip_manifest,
  useMutateUpdateTrip_manifest_bonus,
  useMutateUpdateTrip_manifest_seating_template,
  useMutateUpdateTrip_manifest_status,
} from 'queries/trip_manifest.query';
import { useMutateAdd_fee_to_trip, useMutateRemove_fee_from_trip } from 'queries/trip_manifest_fee.query';
import { TypedVehicle } from 'queries/vehicle.query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PlusIcon, DeleteIcon, HashtagIcon, PlusCircleIcon, QuestionCircleIcon, UploadIcon, RewardIcon, ViewIcon } from '@shopify/polaris-icons';
import TicketInManifest from './ticketManifest';
import QuickSearchUser from 'components/quickSearchUser';
import { ResponsivePopover } from 'components/ResponsivePopover';
import { ENUM_TRIP_FEE, TRIP_FEE_LABELS, useGetOrdersTransactions, useGetOrders } from 'queries/orders.query';
import { getTripStatusBadge } from './tripStatusBadge';
import __ from 'languages/index';
import DeletedTicketsList from './DeletedTicketsList';
import Capacity from 'components/Capacity';
import { useMutateCreateDriverSettlement, useMutateDriverSettlementDelete } from 'queries/driver_settlement.query';
import { useGetTrip_manifests_log } from 'queries/trip_manifest_log.query';

import dayjs from '../../../utils/dayjs';
import { useAuth } from 'context/AuthContext';
import { useNavigate } from 'react-router-dom';

// --- Component con: Danh sách lái xe ---

function DriverList({
  manifest,
  onRemove,
  isRemoving,
  onAddDriver,
  onAddAssistant,
  showAddDriver,
  setShowAddDriver,
  showAddAssistant,
  setShowAddAssistant,
}: {
  manifest: TypedTripManifest;
  onRemove: (id: string) => void;
  isRemoving: boolean;
  onAddDriver: (userId: string) => void;
  onAddAssistant: (userId: string) => void;
  showAddDriver: boolean;
  setShowAddDriver: (v: any) => void;
  showAddAssistant: boolean;
  setShowAddAssistant: (v: boolean) => void;
}) {
  const renderAddPopover = (
    label: string,
    userRole: 'driver' | 'driver_assistant',
    show: boolean,
    setShow: (v: boolean) => void,
    onAdd: (userId: string) => void
  ) => (
    <ResponsivePopover
      title={userRole === 'driver_assistant' ? 'Thêm phụ xe' : 'Thêm tài xế'}
      preferredPosition="below"
      preventCloseOnChildOverlayClick
      autofocusTarget="first-node"
      activator={
        <Tag onClick={() => setShow(true)}>
          <InlineStack align="start" blockAlign="center" gap="200">
            <span>{label}</span>
            <Icon source={PlusIcon} />
          </InlineStack>
        </Tag>
      }
      onClose={() => setShow(false)}
    >
      <QuickSearchUser
        title=""
        placeholder={userRole === 'driver_assistant' ? 'Gõ để tìm phụ xe' : 'Gõ để tìm tài xế'}
        user_role={userRole}
        onClose={(userData: any) => {
          if (userData?.user_id) onAdd(userData.user_id);
          setShow(false);
        }}
      />
    </ResponsivePopover>
  );

  const drivers = manifest.trip_manifest_driver;

  const sortedDrivers = useMemo(() => {
    if (!drivers) return [];
    return [...drivers].sort((driverA, driverB) => {
      const roleScore = (driver: any) => (driver?.driver_role === 'driver' ? 0 : 1);
      return roleScore(driverA) - roleScore(driverB);
    });
  }, [drivers]);

  return (
    <BlockStack gap="200">
      {sortedDrivers?.map((driver, idx) => (
        <Box key={driver.id + '____' + idx} padding="200" background="bg-surface-secondary" borderRadius="100">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap={'400'} align="start" blockAlign="center">
              <Text as="span" tone="subdued">
                {driver.driver_role === 'driver' ? 'Tài xế' : 'Phụ xe'}
              </Text>
              <Text as="span" fontWeight="semibold">
                <Link url={`tel:${driver.driver_data?.user_phonenumber || '0'}`}>
                  {driver.driver_data?.display_name} ({driver.driver_data?.user_phonenumber || 'chưa có số điện thoại'})
                </Link>
              </Text>
            </InlineStack>
            <Button icon={DeleteIcon} variant="plain" onClick={() => onRemove(String(driver.id))} loading={isRemoving} />
          </InlineStack>
        </Box>
      ))}
      <Capacity current_user_can={'manage_trip_manifest'} extra_condition={manifest.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED}>
        <InlineStack gap="400">
          {renderAddPopover('Thêm tài', 'driver', showAddDriver, setShowAddDriver, onAddDriver)}
          {renderAddPopover('Thêm phụ', 'driver_assistant', showAddAssistant, setShowAddAssistant, onAddAssistant)}
        </InlineStack>
      </Capacity>
    </BlockStack>
  );
}

// Component con để hiển thị từng chuyến, chứa logic xóa
export default function ManifestItem({
  manifest,
  onNewTicket,
  editButtonCallback,
  editTaxButtonCallback,
  payButtonCallback,
}: {
  manifest: TypedTripManifest;
  onNewTicket: (trip_manifest_id: any, type: string, position?: string[]) => void;
  editButtonCallback: (order_id: any) => void;
  editTaxButtonCallback: (order_id: any) => void;

  payButtonCallback: (order_id: any) => void;
}) {
  const { addNotification } = useNotification();
  const { showDeleteConfirm } = useDeleteConfirm();
  const { currentUserCan } = useAuth();
  const navigate = useNavigate();

  const { mutateAsync: deleteTripManifest } = useMutateDeleteTrip_manifest();
  const { mutateAsync: updateTripManifest } = useMutateUpdateTrip_manifest();
  const { mutateAsync: updateTripManifestSeating } = useMutateUpdateTrip_manifest_seating_template();
  const { mutateAsync: updateTripManifestStatus } = useMutateUpdateTrip_manifest_status();
  const { mutateAsync: updateTripManifestBonus } = useMutateUpdateTrip_manifest_bonus();
  const { mutateAsync: updateTripManifestFee } = useMutateAdd_fee_to_trip();

  // FIX: Thêm hook xóa lái xe và vé, sử dụng mutateAsync
  const { mutateAsync: removeDriver, isPending: isRemovingDriver } = useMutateRemove_driver_from_trip();
  // const { mutateAsync: removeTicket, isPending: isRemovingTicket } = useMutateRemove_ticket_from_trip();
  const { mutateAsync: assignDriverToTrip } = useMutateAssign_driver_to_trip();
  // FIX: Thêm lại nút "Thêm chuyến"
  const { mutateAsync: createManifest, isPending: isCreatingManifest } = useMutateCreateTrip_manifest();

  const handleRemoveDriver = async (trip_manifest_driver_id: string) => {
    try {
      await removeDriver(trip_manifest_driver_id);
      addNotification('info', 'Đã xóa lái xe khỏi chuyến.');
    } catch (_e: any) {}
  };

  /** thêm tài xế ở modal của Hiếu */
  const [showAddNewDriverInMemo, setShowAddNewDriverInMemo] = useState(false);
  /** Thêm tài xế */
  const [showAddNewDriver, setShowAddNewDriver] = useState(null);
  const addNewDriverCallback = useCallback(async (trip_manifest_id: any, user_id: string, role: string) => {
    try {
      await assignDriverToTrip({
        trip_manifest_id: String(trip_manifest_id),
        driver_id: user_id,
        driver_role: role ?? 'driver',
      });
      setShowAddNewDriver(null);
      setShowAddNewDriverInMemo(false);
    } catch (e) {
      //   addNotification('error', 'Cập nhật tài xế bị lỗi');
    }
  }, []);

  /** thêm phụ xe */
  const [showAddNewDriverAssistant, setShowAddNewDriverAssistant] = useState(false);
  /** thêm xe */
  // const [showAddNewCar, setShowAddNewCar] = useState<bigint | string | null>(null);
  const addOrUpdateCarCallback = useCallback(async (manifest_id: any, car_id: string) => {
    try {
      await updateTripManifest({
        id: manifest_id,
        vehicle_id: car_id,
      });
    } catch (e) {
      addNotification('error', 'Cập nhật xe bị lỗi');
    }
  }, []);

  /** xóa xe khỏi chuyến */
  const removeVehicleCallback = useCallback(async (manifest_id: any) => {
    try {
      await updateTripManifest({
        id: manifest_id,
        vehicle_id: null,
      });
      addNotification('info', 'Đã xóa xe khỏi chuyến.');
    } catch (e: any) {
      addNotification('error', e?.message || 'Lỗi khi xóa xe.');
    }
  }, []);

  /** thêm loại hình vận chuyển  */
  const [showAddNewSeatingModal, setShowAddNewSeatingModal] = useState(false);
  const addOrUpdateSeatingCallback = useCallback(
    async (seatingModel: TypedSeating) => {
      //
      try {
        const confirmed = await showDeleteConfirm(
          'Thay đổi loại hình vận chuyển?',
          'Khi thay đổi loại hình vận chuyển, các vé trong chuyến này sẽ tạm thời chuyển sang chế độ chưa vào chuyến.'
        );
        if (confirmed)
          await updateTripManifestSeating({
            id: manifest?.id,
            seating_id: seatingModel?.id,
          });
      } catch (e) {
        addNotification('error', 'Cập nhật loại hình vận chuyển lỗi');
      }
    },
    [manifest]
  );

  /** Thay đổi status cho chuyến ... */
  const [showUpdateStatusForTrip, setShowUpdateStatusForTrip] = useState(false);
  const updateTripManifestStatusCallback = useCallback(
    async (status: string[]) => {
      try {
        await updateTripManifestStatus({
          id: manifest?.id,
          trip_status: Number(status[0]),
        });
      } catch (e) {}
    },
    [manifest]
  );

  /** Thêm bonus cho chuyến */
  const [showBonusForTrip, setShowBonusForTrip] = useState(false);
  const [bonusValue, setBonusValue] = useState(String(`${manifest?.bonus_amount || 0}`));
  const updateBonusForTripCallback = useCallback(
    async (bonus: number) => {
      try {
        await updateTripManifestBonus({
          id: manifest?.id,
          bonus_amount: Number(bonus),
        });
      } catch (e) {}
    },
    [manifest]
  );

  /** Thêm phí */
  /** Thêm phí */
  const [modalAddFee, setModalAddFee] = useState(false);
  const [tripFeeField, setTripFeeField] = useState(0);
  const [tripFeeNameField, setTripFeeNameField] = useState('FUEL_FEE');
  const submitFeeCallback = useCallback(async () => {
    try {
      await updateTripManifestFee({
        trip_manifest_id: manifest?.id,
        trip_fee: tripFeeField,
        trip_fee_description: tripFeeNameField,
      });
      addNotification('info', 'Cập nhật phí thành công!');
      setModalAddFee(false);
    } catch (e) {}
  }, [tripFeeField, tripFeeNameField, manifest]);

  // Xóa phí
  const { mutateAsync: deleteTripFee } = useMutateRemove_fee_from_trip();
  const deleteTripFeeConfirm = useCallback(async (fee_id) => {
    const confirmed = await showDeleteConfirm('Xóa phí này?', 'Bạn có chắc muốn xóa không? Tổng chi phí của chuyến sẽ được tính lại.');
    if (confirmed) {
      try {
        await deleteTripFee(fee_id);
        addNotification('info', 'Đã xóa phí!');
      } catch (e) {}
    }
  }, []);

  // xóa chuyến
  const deleteTripManifestCallback = useCallback(async (trip_manifest_id: any) => {
    const confirmed = await showDeleteConfirm(
      'Xóa chuyến này?',
      'Bạn cần xóa vé ra khỏi chuyến trước mới được xóa. Hành động này không thể khôi phục.'
    );
    if (confirmed) {
      try {
        await deleteTripManifest(trip_manifest_id);
        addNotification('info', 'Xoá chuyến thành công!');
      } catch (e: any) {
        let message = e.message || 'Xóa lỗi, vui lòng thử lại!';
        addNotification('error', message);
      }
    }
  }, []);

  // thêm chuyến trung chuyển
  const addShuttleForTripCallback = useCallback(async (trip_manifest_id) => {
    try {
      await createManifest({ trip_type: 'shuttle', trip_manifest_parent: trip_manifest_id });
      addNotification('info', 'Thêm chuyến trung chuyển thành công!');
    } catch (error: any) {
      addNotification('error', error?.message || 'Có lỗi xảy ra');
    }
  }, []);

  const { data: orderResponse, isLoading: loadingOrders, isError: ordersError } = useGetOrders(
    { trip_manifest_id: String(manifest.id), limit: 400, sort: 'createdAt:asc' },
    Boolean(manifest.id)
  );
  const orders = useMemo(() => orderResponse?.entities ?? [], [orderResponse?.entities]);

  const filteredTickets = useMemo(() => {
    const tickets = orders.flatMap((order) => Array.isArray(order.ticket) ? order.ticket : []);
    const allPSG = tickets.filter((ticket) => ticket.seat_position !== 'trunk');
    const allSHPMNT = tickets.filter((ticket) => ticket.seat_position === 'trunk');
    return {
      seatValue: allPSG?.map((a) => BigInt(a.ticket_id)),
      shippingValue: allSHPMNT?.map((a) => BigInt(a.ticket_id)),
    };
  }, [orders]);

  /** Lấy chuyến phụ */
  const [childItems, setChildItems] = useState<TypedTripManifest[]>([]);
  const { data: tripManifestShuttleData } = useGetTrip_manifests({
    trip_manifest_parent: manifest.id,
    limit: 10,
    // @ts-ignore
    trip_status: '!' + ENUM_TRIP_MANIFEST_STATUS.CANCELLED,
  });

  useEffect(() => {
    if (tripManifestShuttleData) {
      setChildItems(tripManifestShuttleData);
    }
  }, [tripManifestShuttleData]);

  const { mutateAsync: createDriverSettlement, isPending: creatingDriverSettlement } = useMutateCreateDriverSettlement();
  const createDriverSettlementCallback = useCallback(async () => {
    try {
      await createDriverSettlement(manifest?.id);
      addNotification('info', 'Đã tạo quyết toán tài xế!');
    } catch (e) {}
  }, [createDriverSettlement, manifest]);

  /** TAB */

  const [selectedTab, setSelectedTab] = useState(0);
  const handleTabChange = useCallback((selectedTabIndex: number) => setSelectedTab(selectedTabIndex), []);
  const tabs = [
    {
      id: 'all-ticket',
      content: 'Tất cả vé',
    },
    {
      id: 'for-accountant',
      content: 'Kế toán',
    },
    {
      id: 'trip_manifest_logs',
      content: 'Logs',
    },
    {
      id: 'driver_settlement',
      badge: manifest?.driver_settlement?.driver_settlement_status ?? '',
      content: 'Quyết toán',
    },
  ];

  const { mutateAsync: getAllOrdersInManifest, data: ordersInManifestData } = useGetOrdersTransactions();
  const { data: tripManifestLogs = [], isLoading: loadingTripManifestLogs } = useGetTrip_manifests_log(
    tabs[selectedTab].id === 'trip_manifest_logs' ? String(manifest.id) : ''
  );

  useEffect(() => {
    switch (selectedTab) {
      case 0: // tất
        break;
      case 1: // kế toán
        getAllOrdersInManifest(manifest.id);
        break;
    }
  }, [selectedTab, manifest.id]);

  return (
    <>
      <div className="manifest_tickets_wrap">
        <Box padding={'400'} borderColor={'border-focus'} borderWidth="050" background="bg-fill" id={`manifest_${manifest.id}`}>
          <div>
            <BlockStack gap="400">
              <BlockStack gap="200">
                {/* Header */}
                <InlineStack align="space-between" blockAlign="center" gap={'300'}>
                  <InlineStack wrap={false} align="start" blockAlign="center" gap="400">
                    <InlineStack align="start" blockAlign="center">
                      <Icon source={HashtagIcon} tone="subdued" />
                      <Tooltip content={`ID ${String(manifest.id)} / Khởi hành ${manifest.departure_hour}`}>
                        <Text as="h3" variant="headingMd" tone="subdued">
                          {manifest.departure_hour}{' '}
                          {manifest.arrivedOn ? dayjs(Number(manifest.arrivedOn)).tz('Asia/Ho_Chi_Minh').format('/ HH:mm') : null}
                        </Text>
                      </Tooltip>
                    </InlineStack>
                    <SeatIndicator
                      totalSeats={manifest?.seating?.seating_available_count}
                      occupiedSeats={manifest.total_occupied_seats}
                      showHint
                      size={12}
                    />
                  </InlineStack>

                  <InlineStack gap="400">
                    <Capacity current_user_can={'admin'} onFail={null} extra_condition={manifest?.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED}>
                      <Popover
                        preferredPosition="below"
                        preventCloseOnChildOverlayClick
                        active={showBonusForTrip}
                        activator={
                          <Link onClick={() => setShowBonusForTrip(true)}>
                            <Badge tone={manifest?.bonus_amount > 0 ? 'success' : 'enabled'} icon={RewardIcon}>
                              Thưởng
                            </Badge>
                          </Link>
                        }
                        autofocusTarget="first-node"
                        onClose={() => setShowBonusForTrip(false)}
                      >
                        <Card>
                          <Form
                            onSubmit={() => {
                              setShowBonusForTrip(false);
                              updateBonusForTripCallback(__helpers.parseNumeric(bonusValue));
                            }}
                          >
                            <TextField
                              autoComplete="off"
                              label="Thưởng"
                              value={bonusValue}
                              clearButton
                              onClearButtonClick={() => setBonusValue('0')}
                              onChange={(v) => setBonusValue(__helpers.formatNumberWithCommas(v))}
                            />
                          </Form>
                        </Card>
                      </Popover>
                    </Capacity>

                    <Popover
                      preferredPosition="below"
                      preventCloseOnChildOverlayClick
                      active={showUpdateStatusForTrip}
                      activator={<Link onClick={() => setShowUpdateStatusForTrip(true)}>{getTripStatusBadge(manifest.trip_status)}</Link>}
                      autofocusTarget="first-node"
                      onClose={() => setShowUpdateStatusForTrip(false)}
                    >
                      <Card>
                        <ChoiceList
                          title="Cập nhật tình trạng chuyến"
                          choices={__helpers.enumToArray(ENUM_TRIP_MANIFEST_STATUS).map((el) => {
                            return {
                              label: tripStatusLabels[el],
                              value: el.toString(),
                              helpText: tripStatusHelptext[el],
                            };
                          })}
                          selected={[`${manifest.trip_status}`]}
                          onChange={(value) => {
                            updateTripManifestStatusCallback(value);
                            setShowUpdateStatusForTrip(false);
                          }}
                        />
                      </Card>
                    </Popover>
                  </InlineStack>
                </InlineStack>
                {/* Vehicle */}

                <Box>
                  <InlineStack blockAlign="center" gap="400">
                    <InlineStack align="start" blockAlign="center" gap="200">
                      {manifest.trip_status >= ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
                        <Text as="h3" variant="headingLg">
                          {manifest?.vehicle?.license_plate}
                        </Text>
                      ) : (
                        <>
                          <ResponsivePopover
                            title="Thêm xe vào chuyến" // Title cho Modal trên di động
                            preferredPosition="below"
                            preventCloseOnChildOverlayClick
                            autofocusTarget="first-node"
                            activator={
                              <Link monochrome removeUnderline>
                                <div>
                                  {manifest?.vehicle?.license_plate ? (
                                    <InlineStack align="start" blockAlign="center" gap="200">
                                      <Text as="h3" variant="headingLg">
                                        {manifest?.vehicle?.license_plate}
                                      </Text>
                                    </InlineStack>
                                  ) : (
                                    <Text as="h3" variant="headingLg">
                                      Chưa thêm xe
                                    </Text>
                                  )}
                                </div>
                              </Link>
                            }
                          >
                            <QuickSearchVehicle
                              onSelect={function (vehicle: TypedVehicle | null): void {
                                addOrUpdateCarCallback(manifest.id, vehicle.id);
                                // setShowAddNewCar(null);
                              }}
                              title={''}
                            />
                          </ResponsivePopover>
                          <span>
                            {manifest?.vehicle?.license_plate && (
                              <Tooltip content="Xóa xe khỏi chuyến">
                                <Button icon={DeleteIcon} variant="plain" onClick={() => removeVehicleCallback(manifest.id)} />
                              </Tooltip>
                            )}
                          </span>
                        </>
                      )}
                    </InlineStack>

                    {manifest.trip_status >= ENUM_TRIP_MANIFEST_STATUS.FINALIZED ? (
                      <Text as="p">{manifest?.seating?.seating_name || 'Chưa thêm, click để thêm'}</Text>
                    ) : (
                      <ResponsivePopover
                        title="Chọn loại hình vận chuyển" // Title cho Modal trên di động
                        preferredPosition="below"
                        preventCloseOnChildOverlayClick
                        autofocusTarget="first-node"
                        active={showAddNewSeatingModal}
                        activator={
                          <Button variant="plain" disclosure onClick={() => setShowAddNewSeatingModal(true)}>
                            Loại hình: {manifest?.seating?.seating_name || 'Chưa thêm, click để thêm'}
                          </Button>
                        }
                        onClose={() => setShowAddNewSeatingModal(false)}
                      >
                        <QuickSearchSeating
                          current_seating_id={manifest?.seating?.id}
                          title={''}
                          onClose={(v) => {
                            addOrUpdateSeatingCallback(v);
                            setShowAddNewSeatingModal(false);
                          }}
                        />
                      </ResponsivePopover>
                    )}
                  </InlineStack>
                </Box>
                {/** Price */}
                <Box>
                  <InlineStack gap={'400'} align="space-between" blockAlign="center">
                    <BlockStack>
                      <Text as="span" tone="subdued">
                        Tổng
                      </Text>
                      <Text as="span" variant="headingLg">
                        {__helpers.formatNumberWithCommas(manifest.total_fare)}
                      </Text>
                    </BlockStack>
                    <BlockStack>
                      <Text as="span">Chi phí</Text>
                      <Text as="span" variant="headingLg">
                        {__helpers.formatNumberWithCommas(manifest.total_fee)}
                      </Text>
                    </BlockStack>

                    <BlockStack>
                      <Text as="span">Tài xế Phải thu</Text>
                      <Text as="span" variant="headingLg">
                        {__helpers.formatNumberWithCommas(manifest.total_amount_due)}
                      </Text>
                    </BlockStack>
                    <BlockStack>
                      <Text as="span">Nộp lại</Text>
                      <Text as="span" variant="headingLg">
                        {__helpers.formatNumberWithCommas(manifest.total_amount_due - manifest.total_fee)}
                      </Text>
                    </BlockStack>
                    <BlockStack>
                      <Text as="span">Thưởng</Text>
                      <Text as="span" variant="headingLg">
                        +{__helpers.formatNumberWithCommas(manifest.bonus_amount)}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                </Box>
              </BlockStack>
              <Divider />

              <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}></Tabs>

              <Divider />

              {tabs[selectedTab].id === 'all-ticket' && (
                <>
                  {/* Drivers */}
                  <Box>
                    <DescriptionList
                      items={[
                        {
                          term: 'Tài xế',
                          description: (
                            <>
                              <DriverList
                                manifest={manifest}
                                onRemove={handleRemoveDriver}
                                isRemoving={isRemovingDriver}
                                onAddDriver={(userId) => addNewDriverCallback(manifest?.id, userId, 'driver')}
                                onAddAssistant={(userId) => addNewDriverCallback(manifest?.id, userId, 'assistant')}
                                showAddDriver={showAddNewDriverInMemo}
                                setShowAddDriver={setShowAddNewDriverInMemo}
                                showAddAssistant={showAddNewDriverAssistant}
                                setShowAddAssistant={setShowAddNewDriverAssistant}
                              />
                            </>
                          ),
                        },
                        {
                          term: 'Chi phí chuyến',
                          description: (
                            <>
                              <BlockStack align="space-between" gap="400">
                                <Text as="p">{manifest.total_fee > 0 ? `${__helpers.formatNumberWithCommas(manifest.total_fee)}` : 'Không có'}</Text>
                                <Divider />
                                {manifest?.trip_manifest_fee?.map((el, index) => {
                                  return (
                                    <InlineStack align="space-between" blockAlign="center" gap="100" key={index + '_candiba_tripmanifestFee'}>
                                      <Text as="p">
                                        <Text as="span" variant="headingMd" fontWeight="bold">
                                          {__helpers.formatNumberWithCommas(el.trip_fee)}
                                        </Text>
                                        <Text as="span" tone="subdued">
                                          {' - '} {__(`TRIP_FEE_` + el.trip_fee_description)}
                                        </Text>
                                      </Text>
                                      <Button variant="plain" icon={DeleteIcon} onClick={() => deleteTripFeeConfirm(el.id)}></Button>
                                    </InlineStack>
                                  );
                                })}
                                <Capacity
                                  current_user_can={'manage_trip_manifest'}
                                  extra_condition={manifest.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED}
                                >
                                  <Popover
                                    preferredPosition="below"
                                    preventCloseOnChildOverlayClick
                                    activator={
                                      <Button variant="plain" onClick={() => setModalAddFee(true)}>
                                        Thêm phí
                                      </Button>
                                    }
                                    active={modalAddFee}
                                    onClose={() => {
                                      setModalAddFee(false);
                                    }}
                                  >
                                    <Box padding={'400'}>
                                      <Form onSubmit={() => submitFeeCallback()}>
                                        <BlockStack gap="400">
                                          <Select
                                            label="Chọn:"
                                            labelInline
                                            options={__helpers.enumToArray(ENUM_TRIP_FEE).map((el) => {
                                              return {
                                                label: TRIP_FEE_LABELS[el],
                                                value: el,
                                              };
                                            })}
                                            onChange={(v) => setTripFeeNameField(v)}
                                            value={tripFeeNameField}
                                          />
                                          <TextField
                                            autoComplete="off"
                                            value={__helpers.formatNumberWithCommas(tripFeeField)}
                                            label="Số tiền"
                                            onChange={(v) => {
                                              setTripFeeField(__helpers.parseNumeric(v));
                                            }}
                                          />

                                          <Button submit variant="primary" disabled={tripFeeField < 1}>
                                            Cập nhật
                                          </Button>
                                        </BlockStack>
                                      </Form>
                                    </Box>
                                  </Popover>
                                </Capacity>
                              </BlockStack>
                            </>
                          ),
                        },
                      ]}
                    />

                    {/* --- Component con: Danh sách lái xe --- */}
                  </Box>

                  {/* Tickets */}
                  <BlockStack gap="400">
                    <Divider />
                    <Text as="h3" variant="headingMd">
                      Vé khách
                    </Text>
                    {loadingOrders && (
                      <div role="status" aria-label="Đang tải danh sách vé" aria-busy="true">
                        <Grid>
                          {[0, 1].map((index) => (
                            <Grid.Cell key={index} columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                              <div className="ticket_element" style={{ cursor: 'default', pointerEvents: 'none' }}>
                                <BlockStack gap="400">
                                  <SkeletonDisplayText size="small" />
                                  <SkeletonBodyText lines={3} />
                                  <Divider />
                                  <SkeletonBodyText lines={2} />
                                </BlockStack>
                              </div>
                            </Grid.Cell>
                          ))}
                        </Grid>
                      </div>
                    )}
                    {ordersError && <Text as="p" tone="critical">Không thể tải danh sách đơn hàng.</Text>}
                    {/* --- Component con: Danh sách vé --- */}
                    {!loadingOrders && !ordersError && <TicketInManifest
                      orders={orders}
                      ticketData={filteredTickets.seatValue || []}
                      manifest={manifest}
                      type="seat"
                      editTaxButtonCallback={editTaxButtonCallback}
                      editButtonCallback={editButtonCallback}
                      payButtonCallback={payButtonCallback}
                      onNewTicket={onNewTicket}
                    />}

                    <Divider />
                    <Text as="h3" variant="headingMd">
                      Vé hàng
                    </Text>
                    {!loadingOrders && !ordersError && <TicketInManifest
                      orders={orders}
                      ticketData={filteredTickets.shippingValue || []}
                      manifest={manifest}
                      type="shipping"
                      editTaxButtonCallback={editTaxButtonCallback}
                      editButtonCallback={editButtonCallback}
                      payButtonCallback={payButtonCallback}
                      onNewTicket={onNewTicket}
                    />}
                    <br />
                  </BlockStack>
                </>
              )}
              {tabs[selectedTab].id === 'for-accountant' && (
                <>
                  <DataTable
                    hideScrollIndicator
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['ID', 'Đơn hàng', 'Số tiền', 'Loại', 'Giải trình', 'Người thu tiền', 'Ngày thanh toán']}
                    rows={
                      ordersInManifestData
                        ? ordersInManifestData.map((transaction) => {
                            return [
                              transaction.id,
                              transaction.order_id,
                              __helpers.formatNumber(transaction.amount),
                              transaction.payment_method,
                              transaction.description,
                              transaction?.casher_data?.display_name || 'unknown',
                              dayjs(new Date(Number(transaction.createdAt))).format('HH:mm DD-MM-YYYY'),
                            ];
                          })
                        : []
                    }
                  />
                </>
              )}
              {tabs[selectedTab].id === 'trip_manifest_logs' && (
                <>
                  {loadingTripManifestLogs ? (
                    <SkeletonBodyText lines={3} />
                  ) : (
                    <DataTable
                      hideScrollIndicator
                      columnContentTypes={['text', 'text', 'text']}
                      headings={['Sự kiện', 'Nội dung', 'Ngày']}
                      rows={tripManifestLogs.map((log) => [
                        log.event_type || '-',
                        log.event_description || '-',
                        log.createdAt ? dayjs(new Date(Number(log.createdAt))).format('HH:mm DD-MM-YYYY') : '-',
                      ])}
                    />
                  )}
                </>
              )}

              {tabs[selectedTab].id === 'driver_settlement' && currentUserCan('manage_driver_settlement') && (
                <>
                  {manifest?.driver_settlement ? (
                    <>
                      <Box>
                        <InlineStack gap={'400'} align="start" blockAlign="center">
                          <Text as="span">Tổng</Text>
                          <Text as="span" variant="headingLg">
                            {__helpers.formatNumberWithCommas(manifest?.driver_settlement?.total_fare)}
                          </Text>
                          <Text as="span">Chi phí</Text>
                          <Text as="span" variant="headingLg">
                            {__helpers.formatNumberWithCommas(manifest?.driver_settlement?.total_fee)}
                          </Text>
                          <Text as="span">Nộp lại</Text>
                          <Text as="span" variant="headingLg">
                            {__helpers.formatNumberWithCommas(manifest?.driver_settlement?.total_settlement_amount)}
                          </Text>
                          <Text as="span">Lương tài xế</Text>
                          <Text as="span" variant="headingLg">
                            {__helpers.formatNumberWithCommas(manifest?.driver_settlement?.driver_earning_amount)}
                          </Text>
                        </InlineStack>
                      </Box>
                      <DescriptionList
                        items={[
                          {
                            term: 'ID',
                            description: manifest?.driver_settlement?.id,
                          },
                          {
                            term: 'Tình trạng',
                            description: manifest?.driver_settlement?.driver_settlement_status,
                          },
                          {
                            term: 'Ngày tạo',
                            description: dayjs(Number(manifest?.driver_settlement?.createdAt)).tz('Asia/Ho_Chi_Minh').format('HH:mm DD-MM-YYYY'),
                          },
                          {
                            term: 'Lương tài xế',
                            description: manifest?.driver_settlement?.driver_earning_snapshot?.calculation?.formula || '--',
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <div>
                      <Button
                        icon={UploadIcon}
                        disabled={!!manifest?.driver_settlement || manifest?.trip_status !== ENUM_TRIP_MANIFEST_STATUS.FINALIZED}
                        variant="monochromePlain"
                        loading={creatingDriverSettlement}
                        onClick={() => createDriverSettlementCallback()}
                      >
                        Tạo quyết toán ngay!
                      </Button>
                    </div>
                  )}

                  <Capacity current_user_can="manage_driver_settlement" onFail={null} extra_condition={!!manifest?.driver_settlement}>
                    <Divider />
                    <Button
                      variant="monochromePlain"
                      icon={ViewIcon}
                      onClick={() => navigate(`/driver_settlement/${manifest?.driver_settlement?.id}`)}
                    >
                      Xem quyết toán
                    </Button>
                  </Capacity>
                </>
              )}
            </BlockStack>
          </div>
        </Box>
      </div>
      <>
        <InlineStack key={`child-items_${Math.random()}`} gap="400" align="start" blockAlign="center">
          <InlineStack align="start" blockAlign="center">
            <Text as="h2" variant="headingLg" tone="subdued">
              Trung chuyển
            </Text>
            <Tooltip content={'Tài trung chuyển sẽ nhìn thấy toàn bộ khách hàng.'}>
              <Icon source={QuestionCircleIcon} tone="subdued" />
            </Tooltip>
          </InlineStack>
        </InlineStack>
        {__helpers.isEmpty(childItems) && (
          <InlineStack key={`child-items_${manifest?.id}___999999`} gap="400" align="start" blockAlign="center">
            <InlineStack align="start" blockAlign="center">
              <Icon source={HashtagIcon} tone="subdued" />
              <Text as="h3" variant="headingMd" tone="subdued">
                Chưa có chuyến trung chuyển nào
              </Text>
            </InlineStack>
          </InlineStack>
        )}
        {childItems.map((childs, index) => (
          <InlineStack key={`child-items_${manifest?.id}___${index}`} gap="400" align="start" blockAlign="center">
            <InlineStack align="start" blockAlign="center">
              <Icon source={HashtagIcon} tone="subdued" />
              <Text as="h3" variant="headingMd" tone="subdued">
                {String(childs.id)} Trung chuyển cho {childs.departure_hour}
              </Text>
            </InlineStack>
            <InlineStack align="start" blockAlign="center" gap="200">
              <ResponsivePopover
                title="Thêm xe vào chuyến" // Title cho Modal trên di động
                preferredPosition="below"
                preventCloseOnChildOverlayClick
                autofocusTarget="first-node"
                activator={
                  <Link monochrome removeUnderline>
                    <Text as="h3" variant="headingLg">
                      {childs?.vehicle?.license_plate ?? 'Chưa thêm xe'}
                    </Text>
                  </Link>
                }
                // onClose={}
              >
                <QuickSearchVehicle
                  onSelect={function (vehicle: TypedVehicle | null): void {
                    addOrUpdateCarCallback(childs.id, vehicle.id);
                    // setShowAddNewCar(null);
                  }}
                  title={''}
                />
              </ResponsivePopover>

              {childs?.vehicle?.license_plate && (
                <Tooltip content="Xóa xe khỏi chuyến">
                  <Button icon={DeleteIcon} variant="plain" onClick={() => removeVehicleCallback(childs.id)} />
                </Tooltip>
              )}
            </InlineStack>
            {!__helpers.isEmpty(childs.trip_manifest_driver) ? (
              <InlineStack align="start" blockAlign="center" gap="200" key={childs.trip_manifest_driver[0].driver_id + '____' + index}>
                <Text as="p" fontWeight="semibold">
                  Tài xế {childs.trip_manifest_driver[0].driver_data?.display_name}
                </Text>
                <Button icon={DeleteIcon} variant="plain" onClick={() => handleRemoveDriver(String(childs.trip_manifest_driver[0].id))} />
              </InlineStack>
            ) : (
              <ResponsivePopover
                title="Thêm tài vào chuyến phụ" // Title cho Modal trên di động
                preferredPosition="below"
                preventCloseOnChildOverlayClick
                autofocusTarget="first-node"
                activator={
                  <Tag onClick={() => setShowAddNewDriver(childs.id)}>
                    <InlineStack align="start" blockAlign="center" gap="200">
                      <span>Thêm tài</span>
                      <Icon source={PlusCircleIcon} />
                    </InlineStack>
                  </Tag>
                }
                active={showAddNewDriver === childs.id}
                onClose={() => setShowAddNewDriver(null)}
              >
                <QuickSearchUser
                  user_role="driver"
                  title=""
                  onClose={(userData: any) => {
                    let __newUser = userData.user_id;
                    if (__newUser) {
                      addNewDriverCallback(childs.id, __newUser, 'driver');
                    }
                  }}
                />
              </ResponsivePopover>
            )}

            <Text as="p">{getTripStatusBadge(childs.trip_status)}</Text>
            {childs.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED && (
              <Tooltip content={'Xóa chuyến trung chuyển này?'}>
                <Button variant="monochromePlain" icon={DeleteIcon} onClick={() => deleteTripManifestCallback(childs?.id)}></Button>
              </Tooltip>
            )}
          </InlineStack>
        ))}
        <Capacity current_user_can={'manage_trip_manifest'} extra_condition={manifest.trip_status < ENUM_TRIP_MANIFEST_STATUS.FINALIZED}>
          <InlineStack key={`child-items_${manifest?.id}_98`} gap="400" align="start" blockAlign="center">
            <InlineStack align="start" blockAlign="center">
              <Button variant="plain" icon={PlusIcon} onClick={() => addShuttleForTripCallback(manifest?.id)}>
                Thêm trung chuyển
              </Button>
            </InlineStack>
          </InlineStack>
        </Capacity>
      </>
      <br />
      <br />
      <Text as="h3" variant="headingLg" tone="subdued">
        Danh sách các vé đã xóa
      </Text>
      <br />
      <DeletedTicketsList manifest={manifest} />
      <br />
      <br />
    </>
  );
}
